import React from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MegaphoneSimple, ArrowRight, ChartBar, Copy } from "@phosphor-icons/react";

const fetcher = (u) => api.get(u).then(r => r.data);

const archetypeColor = {
  "Pain-Point Driven": "text-destructive",
  "Stat-Heavy": "text-primary",
  "Story-Led": "text-success",
};

function VariantCard({ variant }) {
  const copy = () => {
    navigator.clipboard.writeText(`${variant.headline}\n\n${variant.primary_text}\n\n${variant.description}\n\nCTA: ${variant.cta}`);
    toast.success(`${variant.archetype} copied`);
  };
  return (
    <div className="border border-white/[0.08] bg-[#0a0a0a] rounded-sm p-5 hover-lift hover:border-primary/40 flex flex-col" data-testid={`variant-${variant.archetype.split(" ")[0].toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className={`text-[10px] font-mono uppercase tracking-[0.24em] ${archetypeColor[variant.archetype] || "text-primary"}`}>{variant.archetype}</div>
        <button onClick={copy} className="text-muted-foreground hover:text-primary"><Copy size={14} /></button>
      </div>
      <div className="mt-3 font-heading font-bold text-lg tracking-tight leading-snug">{variant.headline}</div>
      <p className="mt-3 text-sm text-foreground/85 flex-1">{variant.primary_text}</p>
      <p className="mt-2 text-xs text-muted-foreground italic">{variant.description}</p>
      <div className="mt-4 flex items-center justify-between border-t border-white/[0.06] pt-3">
        <div className="text-[10px] font-mono uppercase tracking-widest text-primary">CTA · {variant.cta}</div>
        <div className="text-xs font-mono">
          <span className="text-muted-foreground">CTR </span>
          <span className="text-success">{(variant.predicted_ctr * 100).toFixed(2)}%</span>
        </div>
      </div>
    </div>
  );
}

export default function AgentB() {
  const [product, setProduct] = React.useState("");
  const [metric, setMetric] = React.useState("FHA loan limit increase for 2026");
  const [audience, setAudience] = React.useState("First-time home buyers");
  const [location, setLocation] = React.useState("Memphis");
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const { data: perf, mutate } = useSWR("/agents/copywriter/performance", fetcher);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/agents/copywriter/generate", { product, loan_metric: metric, target_audience: audience, location });
      setResult(r.data);
      mutate();
      toast.success("3 ad variants deployed to A/B micro-test");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground">Agent B</div>
      <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Creative Ad Development Studio</h1>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        Programmatically draft 3 ad archetypes — Pain-Point Driven, Stat-Heavy, Story-Led — wired for Meta and Google Ads.
        Every variant seeds an A/B micro-test into the performance grid below.
      </p>

      <form onSubmit={generate} className="mt-8 border border-white/[0.08] bg-[#0a0a0a] rounded-sm p-6" data-testid="agent-b-form">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-3">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Product / Offer</label>
            <input data-testid="agent-b-product" required value={product} onChange={(e) => setProduct(e.target.value)} placeholder="FHA 30-yr fixed at 5.99%" className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" />
          </div>
          <div className="md:col-span-3">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Current metric / hook</label>
            <input data-testid="agent-b-metric" value={metric} onChange={(e) => setMetric(e.target.value)} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" />
          </div>
          <div className="md:col-span-4">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Target audience</label>
            <input data-testid="agent-b-audience" value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Location</label>
            <input data-testid="agent-b-location" value={location} onChange={(e) => setLocation(e.target.value)} className="w-full bg-transparent border border-white/10 focus:border-primary/50 outline-none px-4 py-3 rounded-sm text-sm" />
          </div>
        </div>
        <button type="submit" disabled={loading || product.length < 3} data-testid="agent-b-generate" className="mt-5 inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground rounded-sm font-medium primary-glow hover-lift disabled:opacity-60 text-sm">
          <MegaphoneSimple size={14} /> {loading ? "Drafting variants…" : "Generate 3 ad archetypes"} <ArrowRight size={14} />
        </button>
      </form>

      {loading && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-60 border border-white/[0.06] bg-[#0a0a0a] rounded-sm animate-pulse" />
          ))}
        </div>
      )}

      {result && (
        <div className="mt-8" data-testid="agent-b-results">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground mb-4">Output · 3 ad archetypes</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {result.variants.map((v, i) => <VariantCard key={i} variant={v} />)}
          </div>
        </div>
      )}

      <div className="mt-10 border border-white/[0.08] bg-[#0a0a0a] rounded-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-muted-foreground">A/B Micro-test Grid</div>
            <div className="mt-1 font-heading font-bold text-lg">Live variant performance</div>
          </div>
          <ChartBar size={18} className="text-primary" />
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead className="text-[10px] uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-white/[0.08]">
                <th className="text-left py-2 pr-4">Archetype</th>
                <th className="text-left py-2 pr-4">Headline</th>
                <th className="text-right py-2 pr-4">Impressions</th>
                <th className="text-right py-2 pr-4">Clicks</th>
                <th className="text-right py-2 pr-4">CTR</th>
                <th className="text-right py-2 pr-4">Spend</th>
                <th className="text-right py-2 pr-4">Conv</th>
                <th className="text-left py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(!perf || perf.length === 0) && (
                <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">No variants yet. Generate ads above to seed the grid.</td></tr>
              )}
              {(perf || []).map((p) => (
                <tr key={p.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className={`py-2 pr-4 ${archetypeColor[p.archetype] || ""}`}>{p.archetype}</td>
                  <td className="py-2 pr-4 truncate max-w-[280px]">{p.headline}</td>
                  <td className="py-2 pr-4 text-right">{p.impressions.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right">{p.clicks.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right text-success">{(p.ctr * 100).toFixed(2)}%</td>
                  <td className="py-2 pr-4 text-right">${p.spend.toFixed(2)}</td>
                  <td className="py-2 pr-4 text-right text-primary">{p.conversions}</td>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 text-[10px] rounded-sm border ${p.status === "active" ? "text-success border-success/40" : "text-destructive border-destructive/40"}`}>{p.status.toUpperCase()}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
